import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

interface AdminUser {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  created_at: string;
  document_count: number;
  last_login?: string;
  status: 'ativo' | 'inativo';
  role: 'admin' | 'user';
}

interface AdminDocument {
  id: string;
  title: string;
  type: string;
  author_email: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  version_count?: number;
}

interface AdminActivity {
  id: string;
  event_type: string;
  description: string;
  user_id: string | null;
  created_at: string;
  metadata: any;
}

interface TemplateUsage {
  template_type: string;
  count: number;
}

interface DailyMetric {
  metric_date: string;
  new_users: number;
  new_documents: number;
  exports: number;
}

interface AdminData {
  totalUsers: number;
  totalDocuments: number;
  totalTemplates: number;
  totalExports: number;
  users: AdminUser[];
  documents: AdminDocument[];
  activities: AdminActivity[];
  templateUsage: TemplateUsage[];
  dailyMetrics: DailyMetric[];
  loading: boolean;
  error: string | null;
}

export function useAdminSimple() {
  const { user, session, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleLoading, setRoleLoading] = useState(true);
  const [adminData, setAdminData] = useState<AdminData>({
    totalUsers: 0,
    totalDocuments: 0,
    totalTemplates: 0,
    totalExports: 0,
    users: [],
    documents: [],
    activities: [],
    templateUsage: [],
    dailyMetrics: [],
    loading: false,
    error: null
  });

  // Check admin role via server-side RPC
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setIsAdmin(false);
      setRoleLoading(false);
      return;
    }

    setRoleLoading(true);

    const checkRole = async () => {
      try {
        const { data, error } = await supabase.rpc('has_role', { _role: 'admin' });
        setIsAdmin(data === true && !error);
      } catch {
        setIsAdmin(false);
      } finally {
        setRoleLoading(false);
      }
    };

    checkRole();
  }, [user, authLoading]);

  const loading = authLoading || roleLoading;

  const fetchAdminData = useCallback(async () => {
    if (!isAdmin || !session) return;

    setAdminData(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Use the edge function which has service role access (bypasses RLS)
      const { data, error } = await supabase.functions.invoke('admin-data', {
        method: 'POST',
        body: { action: 'dashboard' },
      });

      if (error) throw error;

      const users = data.users || data.profiles || [];
      const documents = data.documents || [];
      const activities = data.activities || [];
      const metrics = data.metrics || {};
      const charts = data.charts || {};

      // Users already come fully enriched from the edge function (from auth.users)
      const processedUsers: AdminUser[] = users.map((u: any) => ({
        id: u.user_id,
        user_id: u.user_id,
        email: u.email,
        display_name: u.display_name || u.email || null,
        created_at: u.created_at,
        last_login: u.last_sign_in_at || undefined,
        document_count: u.document_count || 0,
        status: (u.status as 'ativo' | 'inativo') || 'ativo',
        role: (u.role as 'admin' | 'user') || 'user'
      }));

      // Process documents (they come pre-enriched from edge function)
      const processedDocuments: AdminDocument[] = documents.map((d: any) => ({
        id: d.id,
        title: d.title,
        type: d.type,
        author_email: d.author_email,
        user_id: d.user_id,
        created_at: d.created_at,
        updated_at: d.updated_at,
        version_count: d.version_count || 0
      }));

      // Process template usage from charts
      const templateDistribution = charts.templateDistribution || [];
      const processedTemplateUsage: TemplateUsage[] = templateDistribution
        .filter((t: any) => t.count > 0)
        .map((t: any) => ({ template_type: t.name, count: t.count }))
        .sort((a: TemplateUsage, b: TemplateUsage) => b.count - a.count);

      const uniqueTemplatesCount = processedTemplateUsage.length;

      // Process daily metrics from charts
      const dailyMetrics: DailyMetric[] = (charts.docsPerDay || []).map((d: any, i: number) => ({
        metric_date: d.date,
        new_documents: d.count,
        new_users: charts.usersPerDay?.[i]?.count || 0,
        exports: charts.exportsPerDay?.[i]?.count || 0,
      }));

      setAdminData({
        totalUsers: metrics.totalUsers || processedUsers.length,
        totalDocuments: metrics.totalDocuments || documents.length,
        totalExports: metrics.totalExports || 0,
        totalTemplates: uniqueTemplatesCount,
        users: processedUsers,
        documents: processedDocuments,
        activities: activities,
        templateUsage: processedTemplateUsage,
        dailyMetrics: dailyMetrics,
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('Erro ao buscar dados administrativos:', error);
      setAdminData(prev => ({
        ...prev,
        loading: false,
        error: 'Não foi possível carregar alguns dados. Exibindo valores padrão.'
      }));
    }
  }, [isAdmin, session]);

  return {
    isAdmin,
    loading,
    adminData,
    fetchAdminData
  };
}
